import React, { useState } from 'react'
import { Dispatch } from 'redux'
import LoaderButton from '../../../components/loaderButton'
import { Tooltip } from '../../../chassis/tooltip'
import InfoIcon from '../../../assets/images/info.svg'
import { User } from '../../../core/types'
import { resetRefiApplication } from '../../../actions/refiApplicationActions'
import ContactInfoModal from './ContactInfoModal'
import { updateUser, fetchUser } from '../userActions'
import { displayNotification } from '../../../actions/notificationsActions'
import { filterContactFields } from '../../../utils/validationHelpers'
interface ApplyForFinancingBtnProps {
  productPackage: any
  user: User
  dispatch: Dispatch<any>
}

const ApplyForFinancingBtn: React.FC<ApplyForFinancingBtnProps> = ({ 
  productPackage, 
  user, 
  dispatch 
}) => {
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [phoneModalError, setPhoneModalError] = useState<string | null>(null)
  const [phoneModalLoading, setPhoneModalLoading] = useState(false)
  
  const missingFields: string[] = []
  // A "Started" prequal record is just a stub (consumer hasn't completed the
  // application yet), so it must NOT count as an existing application — the
  // button stays enabled so the agent can (re)launch the refi flow. Any other
  // status means a real application exists → disable + show "View Finance
  // Application".
  const refiStatus = productPackage?.refiDetails?.status
  const hasExistingApplication =
    !!productPackage?.refiDetails &&
    Object.keys(productPackage?.refiDetails).length > 0 &&
    refiStatus !== 'Started'
  
  if (!productPackage?.vehicle) {
    missingFields.push('Vehicle')
  }
  if (!user?.firstName) missingFields.push('First Name')
  if (!user?.lastName) missingFields.push('Last Name')
  if (!user?.phone) missingFields.push('Phone')
  if (!user?.email) missingFields.push('Email')

  if (!productPackage?.id) missingFields.push('Package')
  const otherMissingFields = filterContactFields(missingFields)
  const isEnabled = otherMissingFields.length === 0 && !hasExistingApplication

  // contactOverride carries the freshly-entered phone/email from the contact
  // modal. Without it the redirect URL would read the stale `user` prop
  // (email still empty), since fetchUser hasn't re-rendered this component yet.
  const proceedToFinancing = (contactOverride?: { phone?: string; email?: string }) => {
    dispatch(resetRefiApplication() as any)
    if (hasExistingApplication) {
      window.open('https://gravitylending.com/blinker/', '_blank')
      return
    }

    const tokenObj = JSON.parse(localStorage.getItem('accessToken') || 'null')
    const accessToken = tokenObj?.access_token ?? ''

    const vehicle = productPackage?.vehicle
    const address = user?.currentAddress || user?.addresses?.[0]
    const email = contactOverride?.email ?? user?.email
    const phone = contactOverride?.phone ?? user?.phone

    const baseUrl =
      process.env.REACT_APP_REFI_AGENT_URL || 'http://localhost:5179/?view=agent'
    const separator = baseUrl.includes('?') ? '&' : '?'

    const params = new URLSearchParams()
    const appendIfPresent = (key: string, value: any) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    }

    // identifiers / auth
    appendIfPresent('package_id', productPackage?.id)
    appendIfPresent('organization_id', productPackage?.organization?.id)
    appendIfPresent('customer_token', productPackage?.customerToken)
    appendIfPresent('external_user', 'true')
    appendIfPresent('access_token', accessToken)
    // Entity ids so refi-portal can write the agent's edits back to blinker
    // (updateUser / updateAddress / updateVehicle) the same way MissionControl
    // does — see refi-portal/src/lib/blinkerWrite.ts.
    appendIfPresent('user_id', user?.id)
    appendIfPresent('vehicle_id', vehicle?.id)
    appendIfPresent('address_id', address?.id)

    // vehicle prefill. YMMT is resolved the same way the vehicle label is
    // (VehicleWithProductPackageListItem) — the vehicle columns first, then
    // trimSyncMetadata for records whose trim sync populated only that blob.
    // refi-portal keeps showing these after its VIN decode, so a decode that
    // returns a blank field falls back to what MissionControl already knew.
    appendIfPresent('vin', vehicle?.vin)
    appendIfPresent('mileage', vehicle?.mileage)
    appendIfPresent('condition', vehicle?.condition)
    appendIfPresent('year', vehicle?.year ?? vehicle?.trimSyncMetadata?.year)
    appendIfPresent('make', vehicle?.make ?? vehicle?.trimSyncMetadata?.make)
    appendIfPresent('model', vehicle?.model ?? vehicle?.trimSyncMetadata?.model)
    appendIfPresent(
      'trim',
      vehicle?.selectedTrim?.name ?? vehicle?.trimSyncMetadata?.trim
    )

    // customer prefill
    appendIfPresent('first_name', user?.firstName)
    appendIfPresent('last_name', user?.lastName)
    appendIfPresent('phone', phone)
    appendIfPresent('email', email)

    // address prefill
    appendIfPresent('zip_code', address?.zip)
    appendIfPresent('city', address?.city)
    appendIfPresent('state', address?.state)
    appendIfPresent('address_line_1', address?.line1)
    appendIfPresent('address_line_2', address?.line2)

    window.location.href = `${baseUrl}${separator}${params.toString()}`
  }

  const handleApplyForFinancing = () => {
    if (!user?.phone || !user?.email) {
      setShowPhoneModal(true)
      return
    }
    proceedToFinancing()
  }

  const handleConfirmPhone = (contactInfo: { phone: string; email: string }) => {
    setPhoneModalLoading(true)
    setPhoneModalError(null)

    return dispatch<any>(updateUser({ id: user.id, phone: contactInfo.phone, email: contactInfo.email }))
      .then(() => {
        setPhoneModalLoading(false)
        dispatch<any>(displayNotification('Contact information updated'))
        setShowPhoneModal(false)
        setPhoneModalError(null)
        dispatch<any>(fetchUser(user.id))
        // Redirect only AFTER the updateUser response, carrying the just-entered
        // contact so the refi-portal URL has the email/phone.
        proceedToFinancing({ phone: contactInfo.phone, email: contactInfo.email })
      })
      .catch((error) => {
        setPhoneModalLoading(false)
        const errorMessage = error?.message || 'Failed to update contact information'
        setPhoneModalError(errorMessage)
        throw error
      })
  }

  return (
    <div style={{ marginTop: '12px' }}>
      <ContactInfoModal
        isOpen={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false)
          setPhoneModalError(null)
        }}
        onConfirm={handleConfirmPhone}
        description="Please update the customer's contact details to start financing."
        confirmButtonText="Confirm & Continue"
        phone={user.phone}
        email={user.email}
        showPhone={!user?.phone}
        showEmail={!user?.email}
        isLoading={phoneModalLoading}
        serverError={phoneModalError}
      />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Apply for Financing</span>
        <Tooltip
          triggerText={<span><img src={InfoIcon} alt="info" style={{ width: '16px', height: '16px', marginLeft: '8px', verticalAlign: 'middle' }} /></span>}
          theme="dark"
          contentWidth={320}
        >
          <p>Prefills Gravity Lending with this customer and selected vehicle/package.</p>
        </Tooltip>
      </div>
      
      <LoaderButton
        type="button"
        className="button-spacing button-apply-financing button--full-width"
        onClick={handleApplyForFinancing}
        disabled={!isEnabled}
        style={{ marginBottom: '8px' , backgroundColor: !isEnabled ? '#ccc' : undefined, color: !isEnabled ? '#666' : undefined }}
      >
        Apply for Financing
      </LoaderButton>
      
      {hasExistingApplication && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <span 
              style={{ 
                color: '#007bff', 
                textDecoration: 'underline', 
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onClick={handleApplyForFinancing}
            >
              View Finance Application
            </span>
            <span style={{ marginLeft: '8px', fontSize: '14px' }}>✏️</span>
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
            {productPackage?.refiDetails?.created_at && (
              <div>Applied: {new Date(productPackage.refiDetails.created_at).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })} - {new Date(productPackage.refiDetails.created_at).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}</div>
            )}
            <div>Loan Application ID: {productPackage?.refiDetails?.loan_application_no}</div>
            <div>Status: {productPackage?.refiDetails?.status}</div>
          </div>
        </div>
      )}
    </div>
  )
}

ApplyForFinancingBtn.displayName = 'Apply For Financing Button'

export default ApplyForFinancingBtn
